/**
 * Shared utilities and styles for Renderable components
 */

// Editable columns in left-to-right order for arrow key navigation
export const EDITABLE_COLUMNS = [
  "lastVehicle",
  "pulloutLocation",
  "tradeReason",
  "earlyPullIn",
  "pulloutVarianceReason",
  "noteToOperator",
  "signOutComments",
];

/**
 * Try to focus an element, returns true if successful
 */
const tryFocusElement = (columnId, rowIndex) => {
  const elementId = `${columnId}-${rowIndex}`;
  const element = document.getElementById(elementId);

  if (!element) return false;

  // Skip disabled elements
  if (element.disabled) return false;

  // For react-select components, focus the input inside
  const selectInput = element.querySelector("input");
  if (selectInput) {
    selectInput.focus();
    return true;
  }

  // For textarea or checkbox elements
  element.focus();
  return true;
};

/**
 * Navigate to an adjacent cell using arrow keys
 * @param {string} currentColumnId - The current column's id
 * @param {number} currentRowIndex - The current row index
 * @param {string} direction - 'ArrowUp', 'ArrowDown', 'ArrowLeft', or 'ArrowRight'
 * @param {number} rowCount - Total number of rows in the table
 */
export const navigateToCell = (currentColumnId, currentRowIndex, direction, rowCount) => {
  const currentColIndex = EDITABLE_COLUMNS.indexOf(currentColumnId);
  if (currentColIndex === -1) return;

  let targetColIndex = currentColIndex;
  let targetRowIndex = currentRowIndex;

  const colDelta = direction === "ArrowLeft" ? -1 : direction === "ArrowRight" ? 1 : 0;
  const rowDelta = direction === "ArrowUp" ? -1 : direction === "ArrowDown" ? 1 : 0;

  // Try to find a focusable element, skipping disabled ones
  const maxAttempts = EDITABLE_COLUMNS.length;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    targetColIndex += colDelta;
    targetRowIndex += rowDelta;

    // Check boundaries
    if (targetColIndex < 0 || targetColIndex >= EDITABLE_COLUMNS.length) break;
    if (targetRowIndex < 0 || targetRowIndex >= rowCount) break;

    const targetColumnId = EDITABLE_COLUMNS[targetColIndex];
    if (tryFocusElement(targetColumnId, targetRowIndex)) {
      return; // Successfully focused
    }

    // If moving up/down, don't skip columns - stop if element is disabled
    if (rowDelta !== 0) break;
  }
};

export const styles = {
  indicatorSeparator: (base) => ({
    ...base,
    display: "none",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 6px",
    width: "95%",
  }),

  control: (provided, state) => ({
    ...provided,
    height: 32,
    minHeight: 32,
    borderColor: state.isDisabled ? "#ccc" : provided.borderColor,
    color: state.isDisabled ? "black" : provided.color,
    cursor: state.isDisabled ? "not-allowed" : provided.cursor,
    opacity: 1, // Prevent graying out
  }),

  singleValue: (provided, state) => ({
    ...provided,
    fontWeight: state.isDisabled ? "bold" : provided.fontWeight,
    color: state.isDisabled ? "black" : provided.color,
  }),

  container: (base) => ({
    ...base,
    width: "95%",

    paddingLeft: 5,
  }),

  dropdownIndicator: (base) => ({
    ...base,
    display: "none",
  }),

  menu: (provided) => ({
    ...provided,
    width: "auto",
    minWidth: "100%",
    borderBottom: "1px dotted pink",
    padding: 1,
  }),

  option: (provided) => ({
    ...provided,
    whiteSpace: "nowrap",
  }),

  // We need to do this to remove the default styles from the react-select input.
  input: () => ({
    visibility: "visible",
    transform: "",
    flex: "1 1 auto",
    gridArea: "1 / 1 / 2 / 3",
    gridTemplateColumns: "0 min-content",
    "&:after": {
      content: 'attr(data-value) " "',
      visibility: "hidden",
      whiteSpace: "pre",
      gridArea: "1 / 2",
      font: "inherit",
      minWidth: "2px",
      border: 0,
      margin: 0,
      outline: 0,
      padding: 0,
    },
    margin: 2,
    paddingBottom: 2,
    paddingTop: 2,
    color: "hsl(0, 0%, 20%)",
    boxSizing: "border-box",
  }),
};

export const startsWithFilter = (option, inputValue) => {
  return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
};

export const handleBusOnEnter = (rowIndex) => {
  const nextBusInput = document.getElementById(
    `pulloutLocation-${rowIndex}`
  );
  if (nextBusInput) {
    const targetElement = nextBusInput.querySelector("div > div > div > input");
    if (targetElement) {
      targetElement.focus();
    }
  }
};

export const handleMouseEnter = (hoverTimeoutRef, setIsHovered) => {
  hoverTimeoutRef.current = setTimeout(() => {
    setIsHovered(true);
  }, 500); // 2-second delay before expanding
};

export const handleMouseLeave = (hoverTimeoutRef, setIsHovered) => {
  clearTimeout(hoverTimeoutRef.current);
  setIsHovered(false);
};
